import { createContext, useState, useEffect } from 'react';

export const ThemeContext = createContext();

// Complete LeetCode-grade design system with all tokens
const themes = {
  light: {
    background: {
      primary: '#ffffff',
      secondary: '#f9fafb',
      tertiary: '#f3f4f6',
      code: '#f7f9fa',
      card: '#ffffff',
      panel: '#f9fafb',
      hover: '#f3f4f6',
    },
    text: {
      primary: '#1a1a1a',
      secondary: '#4b5563',
      tertiary: '#6b7280',
      muted: '#9ca3af',
      accent: '#2563eb',
      success: '#10b981',
      warning: '#f59e0b',
      danger: '#ef4444',
      onAccent: '#ffffff',
    },
    border: {
      primary: '#e5e7eb',
      secondary: '#d1d5db',
      subtle: 'rgba(0, 0, 0, 0.08)',
      focus: '#3b82f6',
      focusRing: 'rgba(59, 130, 246, 0.4)',
    },
    button: {
      primary: {
        bg: '#3b82f6',
        text: '#ffffff',
        hover: '#2563eb',
        active: '#1d4ed8',
      },
      secondary: {
        bg: 'transparent',
        text: '#4b5563',
        hover: 'rgba(0, 0, 0, 0.08)',
        border: 'rgba(0, 0, 0, 0.15)',
      },
      success: {
        bg: '#10b981',
        text: '#ffffff',
        hover: '#059669',
        active: '#047857',
      },
      danger: {
        bg: '#ef4444',
        text: '#ffffff',
        hover: '#dc2626',
        active: '#b91c1c',
      },
      ghost: {
        bg: 'transparent',
        text: '#3b82f6',
        hover: 'rgba(59, 130, 246, 0.1)',
        border: 'rgba(59, 130, 246, 0.25)',
      },
      warning: {
        bg: '#f59e0b',
        text: '#ffffff',
        hover: '#d97706',
        active: '#b45309',
      },
    },
    tag: {
      bg: 'rgba(0, 0, 0, 0.04)',
      text: '#4b5563',
      border: 'rgba(0, 0, 0, 0.08)',
      hover: 'rgba(0, 0, 0, 0.08)',
    },
    input: {
      bg: '#ffffff',
      border: '#d1d5db',
      focus: '#3b82f6',
      placeholder: '#9ca3af',
      text: '#1a1a1a',
    },
    syntax: {
      background: '#f7f9fa',
      text: '#1a1a1a',
      comment: '#6b7280',
      keyword: '#8b5cf6',
      function: '#2563eb',
      string: '#10b981',
      number: '#f59e0b',
      tag: '#ef4444',
    },
    shadow: {
      sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
      md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      none: 'none',
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
    },
    radius: {
      sm: '6px',
      md: '8px',
      lg: '10px',
      xl: '14px',
      pill: '9999px',
    },
    transition: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
    },
  },
  dark: {
    background: {
      primary: '#1a1a1a',        // Main dark background (matching image)
      secondary: '#212121',      // Slightly lighter for cards/panels
      tertiary: '#282828',       // For hover states
      code: '#1e1e1e',           // Code editor background
      card: '#212121',           // Card background
      panel: '#212121',          // Panel background
      hover: '#282828',          // Hover state
    },
    text: {
      primary: '#eff1f6',        // Strong readable (near white)
      secondary: '#b4b8c5',      // Lesser emphasis
      tertiary: '#8b8f9b',       // Muted captions
      muted: '#6b7280',          // Very muted
      accent: '#60a5fa',         // Blue accent
      success: '#22c55e',        // Vivid green for success
      warning: '#f59e0b',        // Warning orange
      danger: '#ef4444',         // Danger red
      onAccent: '#ffffff',       // Text on accent buttons
    },
    border: {
      primary: 'rgba(255, 255, 255, 0.1)',    // Semi-transparent light (10%)
      secondary: 'rgba(255, 255, 255, 0.15)',  // Slightly stronger
      subtle: 'rgba(255, 255, 255, 0.08)',     // Very subtle
      focus: '#60a5fa',                         // Focus blue
      focusRing: 'rgba(96, 165, 250, 0.4)',    // Focus ring with alpha
    },
    button: {
      primary: {
        bg: '#3b82f6',           // Bright blue primary
        text: '#ffffff',
        hover: '#2563eb',
        active: '#1d4ed8',
      },
      secondary: {
        bg: 'transparent',
        text: '#b4b8c5',
        hover: 'rgba(255, 255, 255, 0.08)',
        border: 'rgba(255, 255, 255, 0.15)',
      },
      success: {
        bg: '#10b981',           // Emerald green for Run/Submit
        text: '#ffffff',
        hover: '#059669',
        active: '#047857',
      },
      danger: {
        bg: '#ef4444',
        text: '#ffffff',
        hover: '#dc2626',
        active: '#b91c1c',
      },
      ghost: {
        bg: 'transparent',
        text: '#3b82f6',
        hover: 'rgba(59, 130, 246, 0.1)',
        border: 'rgba(59, 130, 246, 0.25)',
      },
      warning: {
        bg: '#f59e0b',
        text: '#ffffff',
        hover: '#d97706',
        active: '#b45309',
      },
    },
    tag: {
      bg: 'rgba(255, 255, 255, 0.06)',         // Translucent dark
      text: '#b4b8c5',
      border: 'rgba(255, 255, 255, 0.1)',
      hover: 'rgba(255, 255, 255, 0.1)',
    },
    input: {
      bg: '#1a1a1a',
      border: 'rgba(255, 255, 255, 0.12)',
      focus: '#60a5fa',
      placeholder: '#6b7280',
      text: '#eff1f6',
    },
    syntax: {
      background: '#1a1a1a',
      text: '#eff1f6',
      comment: '#6b7280',
      keyword: '#a78bfa',
      function: '#60a5fa',
      string: '#34d399',
      number: '#fbbf24',
      tag: '#f87171',
    },
    shadow: {
      sm: '0 1px 2px rgba(0, 0, 0, 0.4)',
      md: '0 2px 4px rgba(0, 0, 0, 0.3)',
      lg: '0 4px 8px rgba(0, 0, 0, 0.3)',
      none: 'none',
    },
    spacing: {
      xs: '4px',
      sm: '8px',
      md: '16px',
      lg: '24px',
      xl: '32px',
    },
    radius: {
      sm: '6px',
      md: '8px',
      lg: '10px',
      xl: '14px',
      pill: '9999px',
    },
    transition: {
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
    },
  },
};

/**
* Provides theme context, synchronizes theme mode with localStorage, and applies CSS variables to :root.
* @example
* ThemeProvider(<App />)
* <ThemeContext.Provider value={{ theme, themeMode, toggleTheme }}>{children}</ThemeContext.Provider>
* @param {{ReactNode}} {children} - Content rendered within the theme context provider.
* @returns {{JSX.Element}} The provider element that supplies theme data to descendant components.
**/
export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState(localStorage.getItem('theme') || 'dark');
  const theme = themes[themeMode];

  useEffect(() => {
    localStorage.setItem('theme', themeMode);

    if (themeMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Apply CSS variables to :root
    Object.entries(theme).forEach(([category, values]) => {
      Object.entries(values).forEach(([key, value]) => {
        if (typeof value === 'object') {
          Object.entries(value).forEach(([subKey, subValue]) => {
            document.documentElement.style.setProperty(
              `--${category}-${key}-${subKey}`,
              subValue
            );
          });
        } else {
          document.documentElement.style.setProperty(
            `--${category}-${key}`,
            value
          );
        }
      });
    });
  }, [themeMode, theme]);

  const toggleTheme = () => setThemeMode(themeMode === 'light' ? 'dark' : 'light');

  return (
    <ThemeContext.Provider value={{ theme, themeMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}; 